// project import
import { useEffect, useState, ReactNode } from "react";

// material-ui
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Chip,
  Tooltip,
  CardContent,
  Card,
  Collapse,
  Alert,
} from "@mui/material";

// material-ui icons
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// project import
import Button from "components/@extended/LoadingButton";
import HoldMenuItem from "components/buttons/holdMenuItem";
import { useTranslation } from "utils/locales/utilityFunctions";
import ToolbarDivider from "components/display/toolbarDivider";

type ChildFormDialogProps = {
  // Nested dialog
  open: boolean;
  title: string;
  text: string;
  onCancelText?: string;
  onClickText: string;
  matchDownMD: boolean;
  onClick?: () => void;
  onCancel?: () => void;
};

type FormDialogProps = {
  // Dialog
  open: boolean;
  title: string;
  tabs?: React.ReactNode;
  titleChip?: string;
  titleChipTooltip?: string;
  children: ReactNode;
  // Opt in when children own their scroll area (e.g. an editor with fixed tools).
  contentLayout?: "scroll" | "flex";
  loading?: boolean;
  saveDisabled?: boolean;
  cancelDisabled?: boolean;
  captionText?: string;
  errorText?: string;
  onClickText?: string;
  onCancelText?: string;
  clickawayEnabled?: boolean;
  maxWidth: "xs" | "sm" | "md" | "lg" | "xl" | false;
  showDotMenu?: boolean;
  dotMenuItems?: { text: string; fcn: (e: any) => any; props?: any }[];
  hideBackdrop?: boolean;
  allowDocking?: boolean;
  onClick?: () => void;
  onCancel?: () => void;
  toggleDocking?: (state: boolean) => void;
  // Nested dialog
  openNestedDialog?: boolean;
  titleNestedDialog?: string;
  textNestedDialog?: string;
  onClickNestedDialog?: () => void;
  onCancelNestedDialog?: () => void;
};

// Nested child dialog
function ChildDialog(props: ChildFormDialogProps) {
  return (
    <Dialog open={props.open} maxWidth="sm">
      <Box sx={{ p: !props.matchDownMD ? 1 : 0 }}>
        <DialogTitle>{props.title}</DialogTitle>

        <DialogContent dividers>{props.text}</DialogContent>

        <DialogActions>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ width: 1, px: 1.5, py: 0.75 }}
          >
            <Button color="secondary" onClick={props.onCancel}>
              {props.onCancelText}
            </Button>

            <Button variant="contained" onClick={props.onClick} sx={{ bgcolor: "primary.light" }}>
              {props.onClickText}
            </Button>
          </Stack>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

const FormDialog = (props: FormDialogProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const theme = useTheme();
  // Styling
  const matchDownMD = useMediaQuery(theme.breakpoints.down("md"));
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  const supportsDVH = CSS.supports('height', '100dvh');
  const maxHeight = supportsDVH ? !matchDownMD ? 'calc(100dvh - 204px)' : 'calc(100dvh - 196px)' : !matchDownMD ? 'calc(100vh - 204px)' : 'calc(100vh - 196px)';

  // --- State and refs --- //
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openDotMenuItems = Boolean(anchorEl);
  const [disabled, setDisabled] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Dialog for cancel warning etc.
  let openChildDialog = false;
  if (props.openNestedDialog) {
    openChildDialog = true;
  }

  useEffect(() => {
    if (!props.open && anchorEl) setAnchorEl(null);
  }, [props.open]);

  // Clear state when it closes
  useEffect(() => {
    if (!props.open) {
      setIsMinimized(false);
    }
  }, [props.open]);

  // To prevent flicking when loading is controlling button disable
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (props.saveDisabled) {
      timer = setTimeout(() => {
        setDisabled(true);
      }, 200);
    } else {
      setDisabled(false);
      if (timer) clearTimeout(timer);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [props.saveDisabled]);

  // Dot menu actions
  const handleDotMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseDotMenuItems = () => {
    setAnchorEl(null);
  };

  const handleMinimize = () => {
    if (props.toggleDocking) {
      props.toggleDocking(true);
    }
    setIsMinimized(true);
  };

  const handleRestore = () => {
    if (props.toggleDocking) {
      props.toggleDocking(false);
    }
    setIsMinimized(false);
  };

  return (
    <>
      <Dialog
        open={props.open && !isMinimized}
        maxWidth={props.maxWidth}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            pointerEvents: 'auto', // Re-enables modal interaction
            display: 'flex',
            flexDirection: 'column'
          },
          pointerEvents: isMinimized ? 'none' : 'auto'
        }}
        hideBackdrop={props.hideBackdrop || isMinimized}
        onClose={props.clickawayEnabled ? props.onCancel : undefined}
      >
        <Box sx={{
          p: !matchDownMD ? 1 : 0,
          ...(props.contentLayout === "flex" && {
            display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden",
          }),
        }}>
          {/* mobile */}
          {matchDownSM && (
            <Stack alignItems="flex-start" sx={{ width: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: "100%", pr: 2, minWidth: 0 }}
              >
                {/* Title + Tabs */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    flexGrow: 1,
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <DialogTitle
                    sx={{
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      mb: props.tabs ? -0.5 : 0,
                    }}
                  >
                    {props.title}
                  </DialogTitle>

                  {props.tabs && <Box sx={{ height: 34, mb: 1 }}>
                    <ToolbarDivider orientation={"horizontal"} />
                  </Box>}

                  <Box
                    sx={{
                      overflowX: "auto",
                      flexGrow: 1,
                      minWidth: 0,
                      mt: 2
                    }}
                  >
                    {props.tabs}
                  </Box>
                </Box>

                {/* Minimize button */}
                {props.allowDocking && (
                  <IconButton
                    onClick={handleMinimize}
                    aria-label="show more"
                    sx={{ bgcolor: "transparent", mt: props.tabs ? 2 : 0 }}
                  >
                    <ExpandMoreIcon fontSize="small" color="primary" />
                  </IconButton>
                )}
              </Stack>

              {/* Chip under titel og tabs */}
              {props.titleChip && (
                <Tooltip title={props.titleChipTooltip}>
                  <Chip
                    label={props.titleChip}
                    color="primary"
                    variant="combined"
                    size="small"
                    sx={{ ml: 3, mb: 2, mt: -1 }}
                  />
                </Tooltip>
              )}
            </Stack>
          )}

          {/* desktop */}
          {!matchDownSM && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pr: 2, pl: 0, width: "100%", minWidth: 0 }}
            >
              {/* Left section: Title + Tabs */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  flexGrow: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <DialogTitle
                  sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    mb: props.tabs ? -0.5 : 0,
                  }}
                >
                  {props.title}
                </DialogTitle>

                {props.tabs && <Box sx={{ height: 34, mb: 1 }}>
                  <ToolbarDivider orientation={"horizontal"} />
                </Box>}

                <Box
                  sx={{
                    overflowX: "auto",
                    flexGrow: 1,
                    minWidth: 0,
                    mt: 1
                  }}
                >
                  {props.tabs}
                </Box>
              </Box>

              {/* Right section: Chip + Minimize */}
              <Stack direction="row" spacing={1} alignItems="center">
                {props.titleChip && (
                  <Tooltip title={props.titleChipTooltip}>
                    <Chip
                      label={props.titleChip}
                      color="primary"
                      variant="combined"
                      size="small"
                    />
                  </Tooltip>
                )}

                {props.allowDocking && (
                  <IconButton
                    onClick={handleMinimize}
                    aria-label="show more"
                    sx={{ bgcolor: "transparent" }}
                  >
                    <ExpandMoreIcon fontSize="small" color="primary" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          )}

          <DialogContent
            dividers
            sx={{
              maxHeight: maxHeight,
              overflowY: 'auto',
              ...(props.contentLayout === "flex" && {
                height: maxHeight,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }),
            }}
          >
            {props.captionText && (
              <Box sx={{ pb: 2 }}>
                <Typography variant="caption" color="GrayText">
                  {props.captionText}
                </Typography>
              </Box>
            )}

            {props.children}
          </DialogContent>

          <DialogActions>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems={"center"}
              sx={{ width: 1, pl: 1.5, pr: props.showDotMenu ? 0.5 : 1.5, py: 0.75 }}
            >
              {props.errorText && props.errorText !== "" && (
                <Alert severity="error" sx={{ mr: "auto", py: 0 }}>
                  {props.errorText}
                </Alert>
              )}

              {props.onCancel && (
                <Button color="secondary" disabled={props.cancelDisabled ||props.loading} onClick={props.onCancel}>
                  {props.onCancelText}
                </Button>
              )}

              {props.onClick && (
                <div className="form-dialog-main-button">
                  <Button
                    variant="contained"
                    disabled={disabled || props.loading}
                    loading={props.loading}
                    sx={{
                      bgcolor: "primary.light",
                      "&.Mui-disabled": {
                        bgcolor: "secondary.lighter",
                        color: "secondary.main",
                      },
                    }}
                    onClick={props.onClick}
                  >
                    {props.onClickText}
                  </Button>
                </div>
              )}

              {props.showDotMenu && (
                <IconButton
                  disabled={props.loading}
                  aria-label="settings"
                  onClick={handleDotMenuClick}
                >
                  <MoreVertIcon fontSize="small" color="primary" />
                </IconButton>
              )}
            </Stack>
          </DialogActions>
        </Box>

        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={openDotMenuItems}
          onClose={handleCloseDotMenuItems}
          MenuListProps={{
            "aria-labelledby": "lock-button",
            role: "listbox",
          }}
        >
          {props.dotMenuItems?.map((item, idx) => {
            // Check for type
            if (item.props && item.props.type && item.props.type === "hold") {
              return (
                <HoldMenuItem
                  key={"dot-menu-item-" + idx + "-" + item.text}
                  label={item.text}
                  disabled={item.props && item.props.disabled}
                  holdTime={item.props.time ? item.props.time : 1500}
                  onHoldComplete={() => {
                    item.fcn(null);
                    handleCloseDotMenuItems();
                  }}
                />
              );
            }
            // Default type
            return (
              <MenuItem
                key={"dot-menu-item-" + item.text}
                disabled={item.props && item.props.disabled}
                onClick={() => {
                  item.fcn(null);
                  handleCloseDotMenuItems();
                }}
                sx={{
                  "&.Mui-selected": {
                    bgcolor: "primary.lighter",
                  },
                  "&.MuiMenuItem-root:hover": {
                    bgcolor: "secondary.lighter",
                  },
                }}
              >
                {item.text}
              </MenuItem>
            );
          })}
        </Menu>

        <ChildDialog
          open={openChildDialog}
          title={translate("loss-of-data-title")}
          text={translate("loss-of-data-text")}
          onCancelText={translate("loss-of-data-proceed")}
          onClickText={translate("loss-of-data-cancel")}
          onClick={props.onClickNestedDialog}
          onCancel={props.onCancelNestedDialog}
          matchDownMD={matchDownMD}
        />
      </Dialog>

      {isMinimized && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 10,
            left: '50%',
            transform: { xs: 'translateX(-94%)', sm: 'translateX(-30%)' },
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          <Collapse in={isMinimized}>
            <Card
              sx={{
                bgcolor: theme.palette.primary.lighter,
                display: "flex",
                flexDirection: "column",
                cursor: 'pointer'
              }}
            >
              <CardContent sx={{ pt: 2, px: 2.5, mb: -0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={handleRestore}>
                  <Typography variant="h5" sx={{ color: "#262626" }}>
                    {translate("reopen-task-modal-button")}
                  </Typography>

                  <ExpandMoreIcon fontSize='small' color="primary" sx={{ transform: "rotate(180deg)", ml: 0.2 }} />
                </Box>
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      )}
    </>
  );
};

export default FormDialog;
